import type { Skill } from "@ark-bot/protocol";
import { useCallback, useEffect, useState } from "react";

import { api } from "../api.js";
import { useText } from "../i18n.js";
import { SKILLS } from "../text/skills.js";

import { useConfirm } from "./ConfirmDialog.js";

interface Props {
  readonly agentId: string;
  readonly agentName: string;
  /** Bumped by a `skills.changed` event, so one the agent wrote itself appears here. */
  readonly revision: number;
  readonly onClose: () => void;
}

interface Draft {
  readonly id: string | null;
  readonly name: string;
  readonly description: string;
  readonly body: string;
}

const BLANK: Draft = { id: null, name: "", description: "", body: "" };

/**
 * The agent's written procedures (ARCHITECTURE §6.10). Each is a SKILL.md in
 * the workspace: the name and description go into every prompt, the body is
 * read only when the skill turns out to be relevant — so the description is
 * the field that decides whether a skill is ever used.
 */
export function Skills({ agentId, agentName, revision, onClose }: Props): React.JSX.Element {
  const t = useText(SKILLS);
  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [directory, setDirectory] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.skills(agentId);
      setSkills(list.skills);
      setDirectory(list.directory);
      setError(null);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    }
  }, [agentId]);

  useEffect(() => {
    void load();
  }, [load, revision]);

  const confirm = useConfirm();
  const act = async (work: () => Promise<unknown>): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
      return true;
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (draft == null) return;
    const body = { name: draft.name.trim(), description: draft.description.trim(), body: draft.body.trim() };
    const saved = await act(() => (draft.id == null ? api.createSkill(agentId, body) : api.updateSkill(agentId, draft.id, body)));
    if (saved) setDraft(null);
  };

  return (
    <section className="agent-settings skills">
      <div className="settings-head">
        <h2>{t.title}</h2>
        <span className="muted">
          {t.intro(agentName)}
          <code>{directory}</code>
        </span>
        <button type="button" className="head-close" onClick={onClose} aria-label={t.close}>
          ×
        </button>
      </div>

      {error != null ? <div className="settings-error">{error}</div> : null}

      {skills == null ? (
        <div className="muted">{t.loading}</div>
      ) : skills.length === 0 ? (
        <div className="muted">{t.empty}</div>
      ) : (
        <ul className="routine-list">
          {skills.map((skill) => (
            <li key={skill.id} className="routine">
              <div className="routine-line">
                <strong>{skill.name}</strong>
                <span className="muted">{skill.description || t.noDescription}</span>
              </div>
              <div className="routine-prompt">{skill.body.length > 400 ? `${skill.body.slice(0, 400)}…` : skill.body}</div>
              <div className="routine-actions">
                <button type="button" disabled={busy} onClick={() => setDraft({ id: skill.id, name: skill.name, description: skill.description, body: skill.body })}>
                  {t.edit}
                </button>
                <button
                  type="button"
                  className="danger"
                  disabled={busy}
                  onClick={() => {
                    void confirm({ title: t.deleteTitle(skill.name), message: t.deleteMessage, action: t.delete, danger: true }).then((yes) => {
                      if (yes) void act(() => api.deleteSkill(agentId, skill.id));
                    });
                  }}
                >
                  {t.delete}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {draft == null ? (
        <div className="settings-actions">
          <button type="button" onClick={() => setDraft(BLANK)} disabled={skills == null}>
            {t.newSkill}
          </button>
        </div>
      ) : (
        <form className="routine-form" onSubmit={(event) => void save(event)}>
          <label>
            {t.name}
            <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder={t.namePlaceholder} required />
          </label>
          <label>
            {t.whenToUse} <span className="muted">{t.whenToUseHint}</span>
            <input value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} placeholder={t.whenToUsePlaceholder} />
          </label>
          <label>
            {t.instructions}
            <textarea rows={8} value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} placeholder={t.instructionsPlaceholder} required />
          </label>
          <div className="settings-actions">
            <button type="submit" className="primary" disabled={busy}>
              {draft.id == null ? t.create : t.save}
            </button>
            <button type="button" onClick={() => setDraft(null)} disabled={busy}>
              {t.cancel}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
